import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';
import type { searchGraphDeal_AtlassianSearchPage$key } from './__generated__/searchGraphDeal_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	subheading: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
	},
	dealContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
});

export const SearchGraphDeal = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphDeal_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphDeal_AtlassianSearchPage on SearchResultGraphDocument {
				id
				url
				title
				lastModifiedDate
				entity {
					... on ExternalDeal {
						stage
						dealClosedAt
						lastActivity {
							lastActivityAt
							event
						}
						owners {
							thirdPartyUser {
								name
							}
						}
						parent @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalCustomerOrg {
								id
								displayName
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage, formatDate } = useIntl();

	const { id, url, entity, title, lastModifiedDate } = data;

	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;

	const dealClosedAtDate = entity?.dealClosedAt ? new Date(entity.dealClosedAt) : null;
	const formattedDate = dealClosedAtDate
		? formatDate(dealClosedAtDate, {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			})
		: '';
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const closeDateMessage =
		dealClosedAtDate && dealClosedAtDate >= today ? messages.closesOn : messages.closedOn;

	const closeOnDateComponent = (
		<Box xcss={styles.content}>
			<FormattedMessage {...closeDateMessage} values={{ date: formattedDate }} />
		</Box>
	);

	const body = <Stack>{entity?.lastActivity?.event}</Stack>;

	const description = (
		<Box>
			<DescriptionBody description={body} />
			<Box xcss={styles.subheading}>
				<LastModified lastModified={entity?.lastActivity?.lastActivityAt} />
			</Box>
		</Box>
	);

	const ownerName = entity?.owners?.[0]?.thirdPartyUser?.name;
	const stageText = entity?.stage || '';
	const parent = entity?.parent?.displayName;
	const headerMetadataParts = [
		formatMessage(messages.eventLabel),
		['parent', parent],
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			productConfigs={productConfigs}
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.dealContainer}>
			<Box xcss={styles.resultWrapper}>
				<SearchResult
					productConfigs={productConfigs}
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					icon={icon}
					description={description}
					headerMetadataParts={headerMetadataParts}
					title={enrichExcerpt(title)}
					role="none"
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					position={position}
					partial={data}
					appMode={appMode}
					footer={footerComponent}
					hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			</Box>
			<StatusLozengeButton statusText={stageText} popupContent={closeOnDateComponent} />
		</Flex>
	);
};
